import { Context, Inject } from '@nuxt/types/app'
import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser'

import { AuthGetterTypes } from '~/store/account/auth'
import { ActiveUserActions, ActiveUserGetterTypes } from '~/store/user/active'
import { User } from '~/types/types'

// Forward-compatible type for traits
type Traits = Record<string, any>

interface Analytics {
  identify(userId: string, traits: Traits): void
  page(): void
  reset(): void
}

declare module 'vue/types/vue' {
  interface Vue {
    $analytics: Analytics
  }
}

declare module 'vuex/types/index' {
  // skipcq: JS-0387, JS-0356
  interface Store<S> {
    $analytics: Analytics
  }
}

declare module '@nuxt/types' {
  interface Context {
    $analytics: Analytics
  }
}

export default async (context: Context, inject: Inject): Promise<void> => {
  const { store, $config } = context

  const { customerIOKey, customerIOSiteID } = $config
  const isCloudProduction =
    !$config.onPrem && $config.nodeEnv === 'production' && $config.bifrostEnv === 'production'

  const isLoggedIn = store.getters[`account/auth/${AuthGetterTypes.GET_LOGGED_IN}`]

  if (!process.client || !isCloudProduction || !customerIOKey || !customerIOSiteID) {
    const analytics: Analytics = {
      identify: () => {
        // intentionally left empty
      },
      page: () => {
        // Intentionally left empty
      },
      reset: () => {
        // Intentionally left empty
      }
    }
    inject('analytics', analytics)
    return
  }

  const customerIO = AnalyticsBrowser.load(
    {
      writeKey: customerIOKey
    },
    {
      integrations: {
        'Customer.io In-App Plugin': {
          siteId: customerIOSiteID
        }
      }
    }
  )

  const getViewerInfo = async (): Promise<User | undefined> => {
    try {
      await store.dispatch(`user/active/${ActiveUserActions.FETCH_VIEWER_INFO}`)
      const viewerInfo = store.getters[`user/active/${ActiveUserGetterTypes.GET_VIEWER}`]
      return viewerInfo
    } catch (e) {
      return undefined
    }
  }

  const analytics: Analytics = {
    identify: (userId: string, traits: Traits) => {
      customerIO.identify(userId, traits)
    },
    page: () => {
      customerIO.page()
    },
    reset: () => {
      customerIO.reset()
    }
  }

  inject('analytics', analytics)

  if (isLoggedIn) {
    const viewer = await getViewerInfo()
    if (viewer && Object.keys(viewer).length) {
      const { analyticsId, email, firstName, lastName } = viewer

      if (analyticsId && email) {
        analytics.identify(analyticsId, {
          email,
          firstName,
          lastName
        })
      }
    }
  }
}
